/**
 * Item #14 — the facility layout the Guided Start bay picker draws: the
 * shop's bays and parking spots as rectangles in layout coordinates (the same
 * shop_bays / parking_lot_spots geometry the Birdseye page draws).
 *
 * Pure: no React, no Supabase. Geometry only — who is in which bay comes from
 * Guided Start's own choices (physical occupancy), never from here.
 */
import type { GuidedBayChoice } from '@/lib/timer/interaction/guidedStart';

export interface FacilityRect {
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FacilityLayout {
  /** Work bays (active, not parking-category). */
  bays: FacilityRect[];
  /** Parking spots — context only: never selectable in Guided Start. */
  spots: FacilityRect[];
}

export interface LayoutRow {
  id: string;
  name?: string | null;
  label?: string | null;
  bay_number?: number | null;
  position_x?: number | null;
  position_y?: number | null;
  width?: number | null;
  height?: number | null;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** A rect OptiBay can draw: numeric position, positive size. */
export function rectOf(row: LayoutRow, label: string): FacilityRect | null {
  if (!isNum(row.position_x) || !isNum(row.position_y) || !isNum(row.width) || !isNum(row.height)) return null;
  if (row.width <= 0 || row.height <= 0) return null;
  return { id: row.id, label, x: row.position_x, y: row.position_y, w: row.width, h: row.height };
}

/**
 * Parking spots with Birdseye's legacy rule: a spot still at (0,0) whose
 * parking-category bay (same id or same name) has the real coordinates is
 * drawn there.
 */
export function normalizeSpotRows(spots: readonly LayoutRow[], legacyParkingBays: readonly LayoutRow[]): FacilityRect[] {
  const byId = new Map(legacyParkingBays.map((b) => [b.id, b]));
  const byName = new Map(legacyParkingBays.map((b) => [b.name ?? '', b]));
  const out: FacilityRect[] = [];
  for (const s of spots) {
    const legacy = byId.get(s.id) ?? byName.get(s.label ?? '');
    const real = isNum(s.position_x) && isNum(s.position_y) && !(s.position_x === 0 && s.position_y === 0 && legacy);
    const row = !legacy || real ? s : {
      ...s, position_x: legacy.position_x, position_y: legacy.position_y,
      width: s.width ?? legacy.width, height: s.height ?? legacy.height,
    };
    const r = rectOf(row, s.label ?? '');
    if (r) out.push(r);
  }
  return out;
}

/**
 * The map can be the primary selector only when EVERY bay the technician may
 * choose is drawn, and the bays are actually laid out (not all stacked on one
 * point, as an unconfigured shop's defaults are). Otherwise the list is shown.
 */
export function pickerLayoutUsable(layout: FacilityLayout | null | undefined, choices: readonly GuidedBayChoice[]): boolean {
  if (!layout || choices.length === 0) return false;
  const drawn = new Map(layout.bays.map((b) => [b.id, b]));
  const rects = choices.map((c) => drawn.get(c.id));
  if (rects.some((r) => !r)) return false;
  const corners = new Set(rects.map((r) => `${r!.x},${r!.y}`));
  return rects.length === 1 || corners.size > 1;
}

/** Tap targets: no bay drawn smaller than this on screen (px). */
export const MIN_BAY_TAP_PX = 44;

/** The smallest zoom at which every choosable bay is a usable tap target. */
export function minTapZoom(layout: FacilityLayout, choices: readonly GuidedBayChoice[]): number {
  const ids = new Set(choices.map((c) => c.id));
  const sides = layout.bays.filter((b) => ids.has(b.id)).map((b) => Math.min(b.w, b.h));
  if (sides.length === 0) return 0;
  return MIN_BAY_TAP_PX / Math.min(...sides);
}
